import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaDownload, FaTimes, FaExclamationTriangle, FaInfoCircle, FaArrowLeft } from 'react-icons/fa';
import { getSharedProjectFromUrl, getProjectSummary } from '../utils/shareUtils';
import { importSharedProject } from '../api/ideas';
import ComponentLoader from '../components/ComponentLoader';

function SharedProjectViewer({ token, setShowLoginModal }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sharedData, setSharedData] = useState(null);
  const [projectSummary, setProjectSummary] = useState(null);
  const [error, setError] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'import'

  useEffect(() => {
    loadSharedProject();
  }, []);

  const loadSharedProject = () => {
    try {
      const data = getSharedProjectFromUrl();
      if (!data) {
        setError('No valid shared project data found in the URL');
        return;
      }

      setSharedData(data);
      const summary = getProjectSummary(data);
      setProjectSummary(summary);
    } catch (err) {
      console.error('Error loading shared project:', err);
      setError('Failed to load shared project. The link may be corrupted or invalid.');
    }
  };

  const handleImportProject = async () => {
    if (!token) {
      setShowLoginModal(true);
      return;
    }

    try {
      setIsImporting(true);
      setError('');

      // Import the complete project including all components
      const newIdea = await importSharedProject(sharedData, token);
      
      setImportSuccess(true);
      
      // Redirect to the new idea after a delay
      setTimeout(() => {
        navigate(`/idea/${newIdea.id}`);
      }, 1500);

    } catch (err) {
      console.error('Error importing project:', err);
      setError('Failed to import project. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleViewOnly = () => {
    setViewMode('preview');
  };

  const handleGoBack = () => {
    navigate('/');
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
        <div className="bg-[var(--bg-secondary)] rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaExclamationTriangle className="text-red-500 text-2xl" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">Unable to Load Project</h2>
          <p className="text-[var(--text-secondary)] mb-6">{error}</p>
          <button
            onClick={handleGoBack}
            className="px-6 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105 active:scale-95 flex items-center gap-2 mx-auto"
          >
            <FaArrowLeft />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (importSuccess) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
        <div className="bg-[var(--bg-secondary)] rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaDownload className="text-green-500 text-2xl" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">Project Imported Successfully!</h2>
          <p className="text-[var(--text-secondary)] mb-6">The project has been added to your account. Redirecting...</p>
        </div>
      </div>
    );
  }

  if (!sharedData || !projectSummary) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary)]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Header */}
      <div className="bg-[var(--bg-secondary)] border-b border-[var(--border-color)] px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleGoBack}
              className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors rounded-lg hover:bg-[var(--bg-hover)]"
            >
              <FaArrowLeft />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-[var(--text-primary)] flex items-center gap-2">
                {projectSummary.icon} {projectSummary.title}
              </h1>
              <p className="text-sm text-[var(--text-secondary)]">Shared Project Preview</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleViewOnly}
              className="px-4 py-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors rounded-lg hover:bg-[var(--bg-hover)] flex items-center gap-2"
            >
              <FaEye />
              View Only
            </button>
            <button
              onClick={handleImportProject}
              disabled={isImporting}
              className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105 active:scale-95 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaDownload />
              {isImporting ? 'Importing...' : 'Import to My Projects'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6">
        {/* Project Info */}
        <div className="mb-6 p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
          <div className="flex items-start gap-3">
            <FaInfoCircle className="text-[var(--primary)] mt-1 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-medium text-[var(--text-primary)] mb-1">Shared Project</h3>
              <p className="text-sm text-[var(--text-secondary)] mb-3">
                {projectSummary.description || 'No description provided'}
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div>
                  <span className="text-[var(--text-secondary)]">Components:</span>
                  <span className="ml-2 font-medium text-[var(--text-primary)]">
                    {projectSummary.totalComponents}
                  </span>
                </div>
                <div>
                  <span className="text-[var(--text-secondary)]">Component Types:</span>
                  <span className="ml-2 font-medium text-[var(--text-primary)]">
                    {projectSummary.componentTypes}
                  </span>
                </div>
              </div>
              {projectSummary.tags?.length > 0 && (
                <div className="mt-3">
                  <span className="text-[var(--text-secondary)] text-sm">Tags:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {projectSummary.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-[var(--primary)]/10 text-[var(--primary)] rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Components Preview */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">Project Components</h3>
          {sharedData.components && sharedData.components.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {sharedData.components
                .sort((a, b) => (a.position || 0) - (b.position || 0))
                .map((component, index) => (
                  <div
                    key={`${component.id}-${index}`}
                    className="bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] overflow-hidden"
                  >
                    {/* Component Header */}
                    <div className="p-4 border-b border-[var(--border-color)]">
                      <h4 className="font-medium text-[var(--text-primary)]">
                        {component.title || `${component.type} Component`}
                      </h4>
                      <p className="text-xs text-[var(--text-secondary)] mt-1 capitalize">
                        {component.type}
                      </p>
                    </div>
                    
                    {/* Component Preview */}
                    <div className="p-4 max-h-96 overflow-hidden">
                      <ComponentLoader
                        type={component.type}
                        data={component.data}
                        isReadOnly={true}
                        showPreviewMode={true}
                      />
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[var(--text-secondary)]">No components in this project</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SharedProjectViewer;
